import { ReactNode, useCallback, useEffect } from 'react';
import { Box, LinearProgress, Snackbar, Alert, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

import { RADIUS } from '../../../styles/themes';

const stripes = keyframes`
  from { background-position: 1rem 0; }
  to { background-position: 0 0; }
`;

import { useToastNotification } from './useToastNotification';
import { ToastNotificationContext } from './toastNotificationContext';

interface Props {
  children: ReactNode;
}

const WithToastNotification = ({ children }: Props) => {
  const { toast, openToast, closeToast, updateProgress } = useToastNotification();

  useEffect(() => {
    const handleGlobalToast = (e: Event) => {
      const customEvent = e as CustomEvent;
      openToast(customEvent.detail);
    };
    window.addEventListener('global-toast', handleGlobalToast);
    return () => window.removeEventListener('global-toast', handleGlobalToast);
  }, [openToast]);

  const handleProgressUpdate = useCallback(
    (e: Event) => {
      const customEvent = e as CustomEvent<{ progress: number }>;
      updateProgress(customEvent.detail.progress);
    },
    [updateProgress]
  );

  useEffect(() => {
    window.addEventListener('global-toast-progress', handleProgressUpdate);
    return () => window.removeEventListener('global-toast-progress', handleProgressUpdate);
  }, [handleProgressUpdate]);

  const hasProgress = toast.progress !== undefined;

  return (
    <ToastNotificationContext.Provider value={{ openToast }}>
      <Snackbar
        open={toast.isOpen}
        autoHideDuration={hasProgress ? null : toast.closeOnClickOnly ? null : toast.autoHideDuration}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert elevation={6} variant="filled" onClose={closeToast} severity={toast.severity}>
          {toast.message}
          {hasProgress && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={toast.progress}
                  sx={{
                    height: 6,
                    borderRadius: `${RADIUS.md}px`,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: `${RADIUS.md}px`,
                      backgroundColor: '#fff',
                      ...(toast.message === 'Finalizing...' && toast.progress === 100 && {
                        backgroundImage: 'linear-gradient(45deg, rgba(0, 0, 0, 0.15) 25%, transparent 25%, transparent 50%, rgba(0, 0, 0, 0.15) 50%, rgba(0, 0, 0, 0.15) 75%, transparent 75%, transparent)',
                        backgroundSize: '1rem 1rem',
                        animation: `${stripes} 1s linear infinite`
                      })
                    }
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: '#fff', minWidth: 35, textAlign: 'right' }}>
                {Math.round(toast.progress!)}%
              </Typography>
            </Box>
          )}
        </Alert>
      </Snackbar>
      {children}
    </ToastNotificationContext.Provider>
  );
};

export default WithToastNotification;

